import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, DollarSign, Shield, FileText, Handshake, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const MarketMapPage = () => {
  const stateData = [
    { state: 'Alabama', electricity: 'No', gas: 'No', notes: 'Fully regulated markets.' },
    { state: 'Alaska', electricity: 'No', gas: 'No', notes: 'Fully regulated; isolated grids limit competition feasibility.' },
    { state: 'Arizona', electricity: 'No', gas: 'Yes (partial)', notes: 'Gas choice for some customers; electricity deregulation paused in 2000s.' },
    { state: 'Arkansas', electricity: 'No', gas: 'No', notes: 'Fully regulated markets.' },
    { state: 'California', electricity: 'Yes', gas: 'Yes', notes: 'Electricity choice limited post-2000s crisis; gas widely deregulated.' },
    { state: 'Colorado', electricity: 'No', gas: 'Yes (partial)', notes: 'Gas choice for some customers; electricity remains regulated.' },
    { state: 'Connecticut', electricity: 'Yes', gas: 'Yes (commercial only)', notes: 'Full electricity choice; gas limited to commercial/industrial.' },
    { state: 'Delaware', electricity: 'Yes', gas: 'Yes', notes: 'Full choice for both electricity and gas.' },
    { state: 'District of Columbia', electricity: 'Yes', gas: 'Yes', notes: 'Full choice for both electricity and gas.' },
    { state: 'Florida', electricity: 'No', gas: 'Yes (partial)', notes: 'Gas choice in some areas; electricity fully regulated.' },
    { state: 'Georgia', electricity: 'No', gas: 'Yes', notes: 'Pioneered gas deregulation; electricity remains regulated.' },
    { state: 'Hawaii', electricity: 'No', gas: 'No', notes: 'Fully regulated; unique island grid system.' },
    { state: 'Idaho', electricity: 'No', gas: 'No', notes: 'Fully regulated markets.' },
    { state: 'Illinois', electricity: 'Yes', gas: 'Yes', notes: 'Full choice for both electricity and gas.' },
    { state: 'Indiana', electricity: 'No', gas: 'Yes', notes: 'Gas deregulated; electricity remains regulated.' },
    { state: 'Iowa', electricity: 'No', gas: 'Yes (partial)', notes: 'Gas choice for some customers; electricity regulated.' },
    { state: 'Kansas', electricity: 'No', gas: 'No', notes: 'Fully regulated markets.' },
    { state: 'Kentucky', electricity: 'No', gas: 'Yes (partial)', notes: 'Gas choice in some areas; electricity regulated.' },
    { state: 'Louisiana', electricity: 'No', gas: 'Yes (partial)', notes: 'Gas choice for some customers; electricity regulated.' },
    { state: 'Maine', electricity: 'Yes', gas: 'Yes', notes: 'Electricity and Gas deregulated.' },
    { state: 'Maryland', electricity: 'Yes', gas: 'Yes', notes: 'Full choice for both electricity and gas.' },
    { state: 'Massachusetts', electricity: 'Yes', gas: 'Yes', notes: 'Full choice for both electricity and gas.' },
    { state: 'Michigan', electricity: 'Yes (limited, 10% cap)', gas: 'Yes', notes: 'Electricity choice capped; gas fully deregulated.' },
    { state: 'Minnesota', electricity: 'No', gas: 'Yes (with minimum)', notes: 'Fully regulated markets.' },
    { state: 'Mississippi', electricity: 'No', gas: 'No', notes: 'Fully regulated markets.' },
    { state: 'Missouri', electricity: 'No', gas: 'Yes (partial)', notes: 'Gas choice for some customers; electricity regulated.' },
    { state: 'Montana', electricity: 'Yes (partial)', gas: 'Yes (partial)', notes: 'Limited choice for both; mostly for commercial customers.' },
    { state: 'Nebraska', electricity: 'No', gas: 'No', notes: 'Fully regulated; public power state.' },
    { state: 'Nevada', electricity: 'No', gas: 'Yes (partial)', notes: 'Gas choice in some areas; electricity deregulation stalled.' },
    { state: 'New Hampshire', electricity: 'Yes', gas: 'No', notes: 'Electricity deregulated; gas remains regulated.' },
    { state: 'New Jersey', electricity: 'Yes', gas: 'Yes', notes: 'Full choice for both electricity and gas.' },
    { state: 'New Mexico', electricity: 'No', gas: 'Yes (partial)', notes: 'Gas choice for some customers; electricity regulated.' },
    { state: 'New York', electricity: 'Yes', gas: 'Yes', notes: 'Full choice for both electricity and gas.' },
    { state: 'North Carolina', electricity: 'No', gas: 'Yes', notes: 'Partially regulated markets.' },
    { state: 'North Dakota', electricity: 'No', gas: 'No', notes: 'Fully regulated markets.' },
    { state: 'Ohio', electricity: 'Yes', gas: 'Yes', notes: 'Full choice for both electricity and gas.' },
    { state: 'Oklahoma', electricity: 'No', gas: 'Yes (partial)', notes: 'Gas choice in some areas; electricity regulated.' },
    { state: 'Oregon', electricity: 'Yes (commercial only)', gas: 'No', notes: 'Electricity choice for large commercial users; gas regulated.' },
    { state: 'Pennsylvania', electricity: 'Yes', gas: 'Yes', notes: 'Full choice for both electricity and gas; a deregulation success story.' },
    { state: 'Rhode Island', electricity: 'Yes', gas: 'Yes', notes: 'Full choice for both electricity and gas.' },
    { state: 'South Carolina', electricity: 'No', gas: 'Yes', notes: 'Fully regulated markets.' },
    { state: 'South Dakota', electricity: 'No', gas: 'No', notes: 'Fully regulated markets.' },
    { state: 'Tennessee', electricity: 'No', gas: 'No', notes: 'Fully regulated markets.' },
    { state: 'Texas', electricity: 'Yes', gas: 'Yes', notes: 'Full choice for most areas; some co-ops/munis remain regulated.' },
    { state: 'Utah', electricity: 'No', gas: 'Yes', notes: 'Fully regulated markets.' },
    { state: 'Vermont', electricity: 'No', gas: 'No', notes: 'Fully regulated; small market size.' },
    { state: 'Virginia', electricity: 'Yes (partial)', gas: 'Yes', notes: 'Electricity choice limited; gas fully deregulated.' },
    { state: 'Washington', electricity: 'No', gas: 'No', notes: 'Fully regulated markets.' },
    { state: 'West Virginia', electricity: 'No', gas: 'Yes', notes: 'Gas deregulated; electricity remains regulated.' },
    { state: 'Wisconsin', electricity: 'No', gas: 'Yes (with minimum)', notes: 'Gas choice for some customers; electricity regulated.' },
    { state: 'Wyoming', electricity: 'No', gas: 'Yes (partial)', notes: 'Gas choice in some areas; electricity regulated.' },
  ];

  const benefits = [
    { icon: <DollarSign size={28} />, title: 'Savings' },
    { icon: <Shield size={28} />, title: 'Enhanced Protection' },
    { icon: <FileText size={28} />, title: 'Accurate Quotes' },
    { icon: <Handshake size={28} />, title: 'Customized Solutions' },
    { icon: <CreditCard size={28} />, title: 'No Hidden Fees' },
  ];

  const getStatusClass = (status) => {
    if (status === 'Yes') return 'status-yes';
    if (status === 'No') return 'status-no';
    return 'status-partial';
  };

  return (
    <main className="page-content" data-testid="market-map-page">
      {/* Page Header */}
      <section className="page-header-with-image">
        <div className="page-header-overlay"></div>
        <div className="container page-header-content">
          <span className="overline">Market Intelligence</span>
          <h1 className="page-title" style={{marginTop: '0.5rem'}}>Who We Can Help</h1>
          <p className="page-subtitle">
            Explore our interactive Market Map to identify deregulated energy markets and discover opportunities for competitive rates and significant cost savings.
          </p>
        </div>
      </section>

      {/* Market Map Introduction */}
      <section className="section section-light">
        <div className="container">
          <div className="market-intro">
            <p className="market-intro-text">
              In deregulated states, businesses and many homeowners have the power to choose their energy supplier, creating opportunities for competitive rates and significant cost savings. If your facility is located in a deregulated market, Last Mile Advisors can help you compare options, lock in stable rates, and maximize your energy savings.
            </p>
            <p className="market-intro-text">
              Not sure if your state qualifies? Use the map below to check your location -- then reach out to our team to discover how we can support your energy procurement needs.
            </p>
          </div>

          {/* Map Image */}
          <div className="market-map-container">
            <img
              src="https://images.squarespace-cdn.com/content/v1/686d602cff5ea16d9a19eb83/efc28fa5-a3d1-4dce-975b-91cfc4bff0c2/Untitled+%281920+x+1480+px%29+%282%29.png"
              alt="Map of the United States showing regulated and deregulated gas and electricity markets"
              className="market-map-image"
              data-testid="market-map-image"
            />
            <div className="map-legend">
              <div className="legend-item">
                <span className="legend-color legend-regulated"></span>
                <span>Regulated (Gas & Electricity)</span>
              </div>
              <div className="legend-item">
                <span className="legend-color legend-electric-only"></span>
                <span>Deregulated Electricity</span>
              </div>
              <div className="legend-item">
                <span className="legend-color legend-both"></span>
                <span>Deregulated (Gas & Electricity)</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* State Data Table */}
      <section className="section section-dark">
        <div className="container">
          <div className="section-header">
            <span className="overline">Data</span>
            <h2 className="section-title">State-by-State Deregulation Status</h2>
            <p className="section-subtitle">
              Detailed breakdown of electricity and natural gas deregulation by state.
            </p>
          </div>
          <div className="table-container" data-testid="state-table-container">
            <table className="state-table">
              <thead>
                <tr>
                  <th>State</th>
                  <th>Electricity</th>
                  <th>Natural Gas</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {stateData.map((row, index) => (
                  <tr key={index}>
                    <td className="state-name">{row.state}</td>
                    <td>
                      <span className={`status-badge ${getStatusClass(row.electricity)}`}>
                        {row.electricity}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusClass(row.gas)}`}>
                        {row.gas}
                      </span>
                    </td>
                    <td className="notes-cell">{row.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section section-light">
        <div className="container">
          <Card className="benefits-card">
            <CardContent className="benefits-content">
              <h2 className="benefits-title">What You Will Experience Working with Our Team of Experts</h2>
              <div className="benefits-grid">
                {benefits.map((benefit, index) => (
                  <div key={index} className="benefit-item" data-testid={`benefit-item-${index}`}>
                    <div className="benefit-icon">{benefit.icon}</div>
                    <span className="benefit-text">{benefit.title}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section-cta-hero">
        <div className="section-cta-overlay"></div>
        <div className="container section-cta-content">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Explore Your Options?</h2>
            <p className="cta-subtitle">
              Reach out to the Last Mile Advisors team today -- we're here to help you navigate the energy market with confidence.
            </p>
            <Link to="/contact">
              <Button className="btn-primary btn-lg" data-testid="market-map-cta-btn">
                Contact Us
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default MarketMapPage;
